import { createPortal } from "react-dom";
import "./NewsletterDialog.scss";
import tickerNamesConstant from "../../../data/tickerNamesConstant";
import { useEffect, useRef, useState } from "react";
import jsonpPromise from "../../../utils/Common/jsonpPromise";

type NewsletterDialogProps = {
  dialogRef: React.RefObject<HTMLDialogElement>;
};

const NewsletterDialog = ({ dialogRef }: NewsletterDialogProps) => {
  const [formRes, setformRes] = useState<{
    status: "loading" | "ok" | "error";
    message: string | undefined;
  } | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const lockScroll = () => {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    };
    const unlockScroll = () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };

    // MutationObserver catches when the `open` attribute is added by showModal()
    const observer = new MutationObserver(() => {
      dialog.open ? lockScroll() : unlockScroll();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });

    // Also listen to the native close event as a safety net
    dialog.addEventListener("close", unlockScroll);

    return () => {
      observer.disconnect();
      dialog.removeEventListener("close", unlockScroll);
      unlockScroll();
    };
  }, [dialogRef]);

  const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);
    data["group[147][1]"] = "true";
    const postUrl =
      "https://algoanalytics.us21.list-manage.com/subscribe/post-json?u=8af1592f39e046313b4578233&amp;id=f095f5f234&amp;f_id=0005c3e1f0";
    const url = new URL(postUrl);
    Object.keys(data).forEach((key) =>
      url.searchParams.append(key, data[key].toString())
    );
    try {
      setformRes({ status: "loading", message: "Submitting…" });
      const res = (await jsonpPromise(url.toString(), { param: "c" })) as {
        result: string;
        msg: string;
      };
      setformRes({ status: "ok", message: res.msg || "Subscribed!" });
      setTimeout(() => {
        dialogRef.current?.close();
        setformRes(null);
        setIsAgreed(false);
        formRef.current?.reset();
      }, 3000);
    } catch (err) {
      setformRes({ status: "error", message: "Something went wrong. Please try again." });
      setTimeout(() => setformRes(null), 5000);
    }
  };

  let submitBg: string | undefined;
  if (formRes?.status === "ok") submitBg = "#059669";
  else if (formRes?.status === "error") submitBg = "#dc2626";
  else if (formRes?.status === "loading") submitBg = undefined;

  return createPortal(
    <dialog
      ref={dialogRef}
      className="nd-dialog"
      onClose={() => {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      }}
    >
      <div className="nd-shell">
        {/* Close */}
        <button
          className="nd-close"
          onClick={() => dialogRef.current?.close()}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* ── Left branding panel ── */}
        <div className="nd-left">
          <div className="nd-left-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="nd-headline">Stay Ahead of<br/>the Market</h2>
          <p className="nd-tagline">
            Join thousands of investors who get daily AI-powered insights delivered to their inbox.
          </p>
          <ul className="nd-perks">
            <li>
              <span className="nd-perk-dot" />
              Daily stock performance updates
            </li>
            <li>
              <span className="nd-perk-dot" />
              AI-powered market analysis
            </li>
            <li>
              <span className="nd-perk-dot" />
              Personalized for your watchlist
            </li>
          </ul>
          <div className="nd-left-deco" aria-hidden="true">
            <div className="nd-deco-ring nd-deco-ring--1" />
            <div className="nd-deco-ring nd-deco-ring--2" />
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="nd-right">
          <div className="nd-form-heading">Create your subscription</div>

          <form onSubmit={onFormSubmit} ref={formRef} className="nd-form">
            {/* Name row */}
            <div className="nd-row">
              <div className="nd-field">
                <label className="nd-label" htmlFor="nd-fname">First Name</label>
                <input
                  id="nd-fname" type="text" name="FNAME"
                  placeholder="John" className="nd-input" required
                />
              </div>
              <div className="nd-field">
                <label className="nd-label" htmlFor="nd-lname">Last Name</label>
                <input
                  id="nd-lname" type="text" name="LNAME"
                  placeholder="Doe" className="nd-input" required
                />
              </div>
            </div>

            {/* Email */}
            <div className="nd-field">
              <label className="nd-label" htmlFor="nd-email">Email Address</label>
              <div className="nd-input-wrap">
                <svg className="nd-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  id="nd-email" type="email" name="EMAIL"
                  placeholder="john@example.com" className="nd-input nd-input--icon" required
                />
              </div>
            </div>

            {/* Stocks */}
            <div className="nd-field">
              <label className="nd-label">Stocks to track <span className="nd-label-hint">(choose up to 3)</span></label>
              <div className="nd-stock-row">
                {[1, 2, 3].map((n) => (
                  <div className="nd-select-wrap" key={n}>
                    <select name={`STOCK${n}`} className="nd-select">
                      <option value="None">Stock {n}</option>
                      {tickerNamesConstant.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <svg className="nd-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            {/* Checkbox */}
            <label className="nd-check-label" htmlFor="nd-tnc">
              <input
                id="nd-tnc" type="checkbox" name="group[147][1]"
                className="nd-check-input" required
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
              />
              <span className="nd-check-box" />
              <span className="nd-check-text">
                I agree to receive daily email newsletters from AlgoFabric for my chosen stocks.
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              className={`nd-submit ${formRes?.status || ""} ${!isAgreed ? "nd-submit--disabled" : ""}`}
              disabled={!isAgreed || !(formRes === null || formRes.status === "error")}
              style={submitBg ? { background: submitBg } : undefined}
            >
              {formRes?.status === "loading" && (
                <svg className="nd-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"
                    strokeDasharray="32" strokeDashoffset="32">
                    <animate attributeName="stroke-dasharray" dur="1.5s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                    <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                  </circle>
                </svg>
              )}
              {formRes?.status === "ok" && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              <span>{formRes === null ? "Subscribe Now" : formRes.message}</span>
            </button>
          </form>
        </div>
      </div>
    </dialog>,
    document.body
  );
};

export default NewsletterDialog;
